<script setup lang="ts">
/**
 * BoutiqueCartButton — l'accès PERMANENT au panier, présent sur la vitrine et
 * sur la fiche produit.
 *
 * ⚠️ DÉCISION : le bouton est TOUJOURS AFFICHÉ, en état neutre quand le panier
 * est vide (icône seule, pas de pastille). Le masquer aurait reproduit le
 * défaut qu'on corrige : un panier qui n'apparaît qu'une fois rempli
 * n'apprend à personne où le retrouver, et son surgissement décale la mise en
 * page au premier ajout. Une icône panier stable est la convention de tous les
 * marchands (Shopify, Sézane, Decathlon) : sa présence constante EST
 * l'affordance. En contrepartie, l'état vide reste discret — fond gris neutre,
 * aucune pastille, libellé « Panier vide » pour les lecteurs d'écran.
 *
 * ⚠️ PLACEMENT : ce bouton n'est PAS dans `AppHeader`. Celui-ci est une pilule
 * FLOTTANTE (`fixed top-5`) partagée par TOUT le site — pages de don,
 * d'adhésion, de billetterie comprises. Y greffer un panier de boutique
 * l'imposerait à des pages qui n'en ont aucun. Le bouton vit donc dans le flux
 * de la page boutique (en-tête de vitrine / fil d'Ariane de la fiche produit),
 * où il ne peut pas chevaucher la pilule flottante.
 */

const { t } = useI18n();
const props = defineProps<{
  totalItems: number;
  /** `true` = variante compacte (fil d'Ariane de la fiche produit). */
  compact?: boolean;
}>();

const { open } = useShopCartDrawer();

const buttonRef = ref<HTMLElement | null>(null);

const hasItems = computed(() => props.totalItems > 0);

const ariaLabel = computed(() =>
  t(
    "boutique.cartButton.ariaOuvrir",
    { n: props.totalItems },
    hasItems.value ? props.totalItems : 0,
  ),
);

/**
 * On transmet le bouton comme déclencheur : à la fermeture du panneau, le
 * focus lui revient (l'utilisateur au clavier reprend exactement où il était).
 */
function onClick() {
  open(buttonRef.value);
}
</script>

<template>
  <button
    ref="buttonRef"
    type="button"
    data-testid="btn-open-cart"
    :aria-label="ariaLabel"
    class="relative inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl border transition-colors"
    :class="[
      compact ? 'size-11' : 'h-11 px-4',
      hasItems
        ? 'border-transparent text-white'
        : 'border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-[#505870] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#1e1e1e]',
    ]"
    :style="
      hasItems
        ? { background: 'var(--accent, #212121)', color: 'var(--accent-text, #fff)' }
        : undefined
    "
    @click="onClick"
  >
    <UIcon name="i-lucide-shopping-cart" class="size-5 shrink-0" />
    <span v-if="!compact" class="text-sm font-semibold">{{ $t("boutique.cartButton.libelle") }}</span>

    <!-- Pastille du nombre d'articles — uniquement quand il y en a.
         Fond BLANC + chiffre au bleu plateforme `--color-main` (Karim
         2026-08-23) : le bleu foncé d'avant se fondait dans le bleu du bouton,
         le nombre ne ressortait pas. Le blanc tranche sur les deux fonds
         (bouton bleu en variante large, bouton clair en variante compacte).
         ⚠️ DEUX PLACEMENTS, un par variante :
         - variante LARGE : la pastille est DANS le flux, juste après le mot
           « Panier ». En absolu débordant l'angle, elle se lisait mal — à
           cheval sur le bord du bouton ; à côté du libellé, elle se lit d'un
           coup d'œil.
         - variante COMPACTE (fiche produit) : le bouton n'est qu'une icône de
           44px, aucune place dans le flux — la pastille reste en absolu sur
           l'angle, seule solution qui ne recouvre pas l'icône. -->
    <span
      v-if="hasItems"
      class="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold tabular-nums"
      :class="compact
        ? 'absolute -right-1.5 -top-1.5 ring-2 ring-white dark:ring-[#0a0a0a] text-[10px]'
        : ''"
      :style="{ background: '#fff', color: 'var(--color-main, #0c3097)' }"
      aria-hidden="true"
    >
      {{ totalItems }}
    </span>
  </button>
</template>
